import {
  convertMeasure,
  InputFieldType,
  isMeasureWithValue,
  NullableMeasure,
  sumMeasures,
} from '@e2m-customer-portal/shared';
import { addDays, differenceInHours, getTime, isSameDay, startOfDay, subSeconds } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ForecastSlice, ProductService, SpotPriceForecast } from '../../../../../generated/graphql';

function extract(
  slices: readonly ForecastSlice[],
  isFifteenMinutesResolution?: boolean,
  sortedByHour?: boolean,
  deviceMap?: (HTMLInputElement | null)[][],
): [NullableMeasure[], Map<number, NullableMeasure[]>] {
  const allValues: NullableMeasure[] = [];
  const valuesByDate: Map<number, NullableMeasure[]> = new Map();

  let row = 0;
  let column = 0;
  let dstPending = true;
  const numberOfColumns = sortedByHour
    ? (deviceMap?.at(0)?.length ?? Math.round(slices.length / (isFifteenMinutesResolution ? 96 : 24)))
    : 1;
  for (const [index, slice] of slices.entries()) {
    const production =
      slice.hasDowntime || deviceMap
        ? { value: Number(deviceMap?.at(row)?.at(column)?.value ?? 0), unit: 'kW' }
        : slice.fields.find((field) => field.type === InputFieldType.PRODUCTION)?.value;
    if (production) {
      allValues.push(production);
      const values = valuesByDate.get(startOfDay(slice.date).getTime()) ?? [];
      if (!values.length) {
        valuesByDate.set(startOfDay(slice.date).getTime(), values);
      }
      values.push(production);
    }

    // Calculate DST slices that are not shown in the table
    if (
      slice.date.getTimezoneOffset() > slices[0].date.getTimezoneOffset() &&
      slice.date.getTimezoneOffset() > slices[index - numberOfColumns]?.date.getTimezoneOffset()
    ) {
      const dstProduction =
        slices[index - numberOfColumns].hasDowntime || deviceMap
          ? { value: Number(deviceMap?.at(row - 1)?.at(column)?.value ?? 0), unit: 'kW' }
          : slices[index - numberOfColumns].fields.find((field) => field.type === InputFieldType.PRODUCTION)?.value;

      if (dstProduction) {
        allValues.push(dstProduction);
        const values = valuesByDate.get(startOfDay(slices[index - numberOfColumns].date).getTime()) ?? [];
        if (!values.length) {
          valuesByDate.set(startOfDay(slices[index - numberOfColumns].date).getTime(), values);
        }
        values.push(dstProduction);
      }
    }

    // Skip device table cells for DST slices that are missing
    const deviceMapIndex = column + numberOfColumns * row;
    if (
      dstPending &&
      deviceMapIndex > numberOfColumns &&
      slices[index + 1]?.date.getDay() !== slices[deviceMapIndex - numberOfColumns + 1]?.date.getDay()
    ) {
      if (deviceMap?.at(0)?.length === column + 1) {
        row += 1;
        column = 0;
      } else if (deviceMap) {
        column += 1;
      }
      dstPending = false;
    }

    // Wrap around at the end of the row in a weird expression to satisfy Sonar
    if (deviceMap?.at(0)?.length === column + 1) {
      row += 1;
      column = 0;
    } else if (deviceMap) {
      column += 1;
    }
  }

  return [allValues, valuesByDate];
}

interface ProductionReduction {
  getSum: (date?: Date) => NullableMeasure;
  getSumFormatted: (date?: Date) => string;
  getAverage: (date?: Date) => NullableMeasure;
  getAverageFormatted: (date?: Date) => string;
}

export function useProductionReduction(
  slices: readonly ForecastSlice[],
  days: readonly Date[],
  isFifteenMinutesResolution?: boolean,
  deviceMap?: (HTMLInputElement | null)[][],
  options?: { convertToUnit?: string; shouldUseLiveStats?: boolean; slicesAreSortedByHour?: boolean },
): ProductionReduction {
  const { t, i18n } = useTranslation();
  const convertToUnit = options?.convertToUnit ?? 'kW';

  const getResult = useCallback(
    (values: NullableMeasure[], interval?: readonly Date[]): NullableMeasure => {
      if (!values.length) {
        return { value: null, unit: !interval ? convertToUnit : convertToUnit + 'h' };
      }

      const defaultResolutionSum = sumMeasures(...values);

      if (!isMeasureWithValue(defaultResolutionSum)) {
        return { value: null, unit: !interval ? convertToUnit : convertToUnit + 'h' };
      }

      const sum = {
        value: defaultResolutionSum.value / (isFifteenMinutesResolution ? 4 : 1),
        unit: defaultResolutionSum.unit,
      };

      const average = interval
        ? {
            ...sum,
            value:
              sum.value / differenceInHours(startOfDay(addDays(interval[0], interval.length)), startOfDay(interval[0])),
          }
        : undefined;

      return convertMeasure(average ?? sum, convertToUnit);
    },
    [convertToUnit, isFifteenMinutesResolution],
  );

  return useMemo(() => {
    const [allValues, valuesByDate] = extract(
      slices,
      isFifteenMinutesResolution,
      options?.slicesAreSortedByHour,
      options?.shouldUseLiveStats ? deviceMap : undefined,
    );

    const allSum = getResult(allValues);
    const allAverage = getResult(allValues, days);

    const sumByDate = new Map<number, NullableMeasure>();
    const averageByDate = new Map<number, NullableMeasure>();

    for (const [timestamp, values] of valuesByDate.entries()) {
      sumByDate.set(timestamp, getResult(values));
      averageByDate.set(timestamp, getResult(values, [new Date(timestamp)]));
    }

    const getSum = (date?: Date): NullableMeasure => ({
      ...(!date
        ? allSum
        : (sumByDate.get(startOfDay(date).getTime()) ?? {
            value: null,
            unit: convertToUnit + 'h',
          })),
      unit: convertToUnit + 'h',
    });
    const getAverage = (date?: Date): NullableMeasure =>
      !date ? allAverage : (averageByDate.get(startOfDay(date).getTime()) ?? { value: null, unit: convertToUnit });
    const getFormatted =
      (fn: (date?: Date) => NullableMeasure) =>
      (date?: Date): string => {
        const value = fn(date).value;
        if (value == null) return t('SHARED_UNKNOWN');
        return Math.round(value).toLocaleString(i18n.language);
      };

    return {
      getSum,
      getSumFormatted: getFormatted(getSum),
      getAverage,
      getAverageFormatted: getFormatted(getAverage),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slices, options?.shouldUseLiveStats, deviceMap]);
}

export function getAverageSpotPrice(date: Date, spotPrices?: SpotPriceForecast) {
  if (!spotPrices) return;
  const spotPricesForDay = spotPrices.dayAheadForecasts.filter((f) => isSameDay(f.date, date));
  if (spotPricesForDay.length > 0) {
    const spotPrices = spotPricesForDay.map((spot) => spot.price);
    const average = spotPrices.reduce((sum, currentValue) => sum + currentValue, 0) / spotPrices.length;
    return Math.round(average);
  }
}

export function dispatchNativeInputEvent(input: HTMLInputElement | null, value?: string) {
  if (!input || input.readOnly || input.disabled) return;

  // Dispatch synthetic "handleChange" event with new value to cell input
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
  nativeInputValueSetter?.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

export const getProductServicesReleaseDates = (sortedProductServices: ProductService[]) =>
  sortedProductServices?.[0] && {
    start: getTime(subSeconds(sortedProductServices[0].begin.toLocaleString(), 1)),
    end: sortedProductServices.at(-1)?.end
      ? getTime(new Date(sortedProductServices.at(-1)!.end!.toLocaleString()))
      : undefined,
  };
